// กริด 1° × 1° ทั้งโลก (docs/PLAN.md ข้อ 4.1)
// เลขช่อง = แถว × 360 + คอลัมน์ แถว 0 อยู่ใต้สุด (-90°) คอลัมน์ 0 อยู่ที่ลองจิจูด -180°

export const GRID_COLS = 360;
export const GRID_ROWS = 180;
export const CELL_COUNT = GRID_COLS * GRID_ROWS; // 64,800 ใส่ใน uint16 ได้พอดี

export interface LatLon {
  lat: number;
  lon: number;
}

export interface CellBounds {
  south: number;
  north: number;
  west: number;
  east: number;
}

export type DensityLevel = 1 | 2 | 3;

export interface RangeCell {
  cell: number;
  level: DensityLevel;
}

export function cellOf(lat: number, lon: number): number {
  const row = Math.min(GRID_ROWS - 1, Math.max(0, Math.floor(lat + 90)));
  const col = Math.floor((((lon + 180) % 360) + 360) % 360) % GRID_COLS;
  return row * GRID_COLS + col;
}

export function cellBounds(cell: number): CellBounds {
  const south = Math.floor(cell / GRID_COLS) - 90;
  const west = (cell % GRID_COLS) - 180;
  return { south, north: south + 1, west, east: west + 1 };
}

export function cellCenter(cell: number): LatLon {
  const b = cellBounds(cell);
  return { lat: b.south + 0.5, lon: b.west + 0.5 };
}

/** ช่องรอบๆ ในระยะ radius ช่อง ไม่รวมตัวเอง ข้ามเส้นวันได้ แต่ไม่ข้ามขั้วโลก */
export function neighbors(cell: number, radius = 1): number[] {
  const row = Math.floor(cell / GRID_COLS);
  const col = cell % GRID_COLS;
  const out: number[] = [];
  for (let dr = -radius; dr <= radius; dr++) {
    const r = row + dr;
    if (r < 0 || r >= GRID_ROWS) continue;
    for (let dc = -radius; dc <= radius; dc++) {
      if (dr === 0 && dc === 0) continue;
      const c = (((col + dc) % GRID_COLS) + GRID_COLS) % GRID_COLS;
      out.push(r * GRID_COLS + c);
    }
  }
  return out;
}

const EARTH_RADIUS_KM = 6371;

export function haversineKm(a: LatLon, b: LatLon): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLon = (b.lon - a.lon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

// รูปแบบ BLOB ใน species_range.cells: เรียงเลขช่องจากน้อยไปมาก
// แต่ละช่องเก็บเป็น varint ของ (ระยะห่างจากช่องก่อนหน้า × 4 + ระดับความหนาแน่น)

export function encodeCells(cells: readonly RangeCell[]): Uint8Array {
  const sorted = [...cells].sort((a, b) => a.cell - b.cell);
  const bytes: number[] = [];
  let prev = -1;
  for (const { cell, level } of sorted) {
    if (!Number.isInteger(cell) || cell < 0 || cell >= CELL_COUNT) throw new RangeError(`เลขช่องไม่ถูกต้อง: ${cell}`);
    if (level !== 1 && level !== 2 && level !== 3) throw new RangeError(`ระดับความหนาแน่นไม่ถูกต้อง: ${level}`);
    if (cell === prev) throw new RangeError(`เลขช่องซ้ำ: ${cell}`);
    let value = (cell - prev - 1) * 4 + level;
    prev = cell;
    while (value >= 0x80) {
      bytes.push((value & 0x7f) | 0x80);
      value = Math.floor(value / 0x80);
    }
    bytes.push(value);
  }
  return Uint8Array.from(bytes);
}

export function decodeCells(bytes: Uint8Array): RangeCell[] {
  const out: RangeCell[] = [];
  let prev = -1;
  let value = 0;
  let scale = 1;
  for (const byte of bytes) {
    value += (byte & 0x7f) * scale;
    if (byte & 0x80) {
      scale *= 0x80;
      continue;
    }
    const level = value % 4;
    const cell = prev + 1 + (value - level) / 4;
    if (level === 0 || cell >= CELL_COUNT) throw new RangeError("ข้อมูลถิ่นที่อยู่เสียหาย");
    out.push({ cell, level: level as DensityLevel });
    prev = cell;
    value = 0;
    scale = 1;
  }
  if (scale !== 1) throw new RangeError("ข้อมูลถิ่นที่อยู่ถูกตัดกลางทาง");
  return out;
}
